import axios from 'axios';

const serviceNowBaseUrl = 'https://servicenow-demo.visionintelligence.xai.insight.com';
const salesforceBaseUrl = 'https://salesforce-demo.visionintelligence.xai.insight.com';

// Create axios instances for each service
const serviceNowApi = axios.create({
  baseURL: serviceNowBaseUrl,
});

const salesforceApi = axios.create({
  baseURL: salesforceBaseUrl,
});

// Add request interceptor to both instances to add the auth token
const addAuthHeader = (config, token) => {
  if (!token) {
    console.error('No token provided to addAuthHeader');
    return Promise.reject(new Error('No authentication token available'));
  }
  config.headers.Authorization = `Bearer ${token}`;
  return config;
};

// Add response interceptors to handle common errors
const addResponseInterceptors = (instance, serviceName) => {
  instance.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;
      
      // Handle token expiration
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          // Trigger token refresh through AuthContext
          const refreshedToken = await window.refreshAuthToken?.(serviceName);
          if (refreshedToken) {
            originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
            return instance(originalRequest);
          }
        } catch (refreshError) {
          console.error(`Token refresh failed for ${serviceName}:`, refreshError);
        }
      }
      
      // Handle rate limiting
      if (error.response?.status === 429 && !originalRequest._rateRetry) {
        const retryAfter = error.response.headers['retry-after'] || 1;
        originalRequest._rateRetry = true;
        await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
        return instance(originalRequest);
      }
      
      // Handle network errors with exponential backoff
      if (!error.response && !originalRequest._networkRetry) {
        const retryCount = originalRequest._networkRetryCount || 0;
        if (retryCount < 3) {
          originalRequest._networkRetry = true;
          originalRequest._networkRetryCount = retryCount + 1;
          const delay = Math.pow(2, retryCount) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          return instance(originalRequest);
        }
      }
      
      return Promise.reject(error);
    }
  );
};

// Keep track of interceptors so we can remove them before adding new ones
let serviceNowInterceptorId = null;
let salesforceInterceptorId = null;

// Add response interceptors to both instances
addResponseInterceptors(serviceNowApi, 'servicenow');
addResponseInterceptors(salesforceApi, 'salesforce');

export const ServiceNowAPI = {
  setToken: (token) => {
    // Remove existing interceptor if there is one
    if (serviceNowInterceptorId !== null) {
      serviceNowApi.interceptors.request.eject(serviceNowInterceptorId);
    }
    // Add new interceptor and store its ID
    serviceNowInterceptorId = serviceNowApi.interceptors.request.use(
      config => addAuthHeader(config, token),
      error => {
        console.error('ServiceNow API request interceptor error:', error);
        return Promise.reject(error);
      }
    );
  },
  getTickets: async () => {
    try {
      const response = await serviceNowApi.get('/troubleticket');
      // Transform the response data to match the component's expectations
      const tickets = Array.isArray(response.data) ? response.data : [response.data];
      return tickets.map(ticket => ({
        id: ticket.id || ticket.Id,
        number: `TICKET-${(ticket.id || ticket.Id).substring(0, 6)}`,
        title: ticket.Description?.split('\n')[0] || 'No Title',
        description: ticket.Description || 'No Description',
        priority: ticket.Priority || 'Medium',
        status: ticket.Status || 'Open',
        location: ticket.Location || 'Unknown',
        createDate: ticket.CreatedDate || new Date().toISOString(),
      }));
    } catch (error) {
      console.error('Error fetching tickets:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.message || 'Failed to fetch tickets');
    }
  },
};

export const SalesforceAPI = {
  setToken: (token) => {
    // Remove existing interceptor if there is one
    if (salesforceInterceptorId !== null) {
      salesforceApi.interceptors.request.eject(salesforceInterceptorId);
    }
    // Add new interceptor and store its ID
    salesforceInterceptorId = salesforceApi.interceptors.request.use(
      config => addAuthHeader(config, token),
      error => {
        console.error('Salesforce API request interceptor error:', error);
        return Promise.reject(error);
      }
    );
  },
  getInventory: async () => {
    try {
      const response = await salesforceApi.get('/inventoryitem');
      const inventories = Array.isArray(response.data) ? response.data : [response.data];
      return inventories.map(item => ({
        id: item.id || item.Id,
        sku: item.sku || item.Sku || 'Unknown',
        description: item.description || item.Description || 'No Description',
        quantity: item.quantity || item.Quantity || '0',
        price: item.price || item.Price || '0.00',
      }));
    } catch (error) {
      console.error('Error fetching inventory:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.message || 'Failed to fetch inventory');
    }
  },
  getLeads: async () => {
    try {
      const response = await salesforceApi.get('/customerlead');
      const rawLeads = response.data?.value || response.data || [];
      const leads = Array.isArray(rawLeads) ? rawLeads : [rawLeads];
      console.log('Raw leads data:', leads); // Debug log
      return leads.map(lead => ({
        id: lead.id || lead.Id,
        firstName: lead.firstName || lead.FirstName || '',
        lastName: lead.lastName || lead.LastName || '',
        email: lead.email || lead.Email || '',
        company: lead.company || lead.Company || '',
        status: lead.status || lead.Status || 'New',
      }));
    } catch (error) {
      console.error('Error fetching leads:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.message || 'Failed to fetch leads');
    }
  },
};
